import OLCollection from 'ol/Collection'
import OLLayerGroup from 'ol/layer/Group'

import type OLLayer from 'ol/layer/Layer'

import { createOlLayerFromLayerConf } from 'config/config'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { MapLayer } from 'config/MapLayer'
import { setLayerSelected, setLayerUnselected } from 'drawer/tabs/layers/actions'

export class MapLayerFolder extends MapLayer {
  static async createLayer(layerConf: ConfigLayerOptions) {
    this.preLayer(layerConf)

    try {
      const olLayer = await this.createNestedLayerGroups(layerConf)
      return olLayer
    } catch (error) {
      console.error('MapLayerFolder createLayer - Unexpected error', layerConf?.titleid, error)
    }

    return null
  }

  private static async createNestedLayerGroups(layerConf: ConfigLayerOptions) {
    // console.debug('createNestedLayerGroups - capabilityOptions', capabilityOptions.titleid, layerConf?.titleid, layerConf)

    if (!layerConf) {
      console.log('createNestedLayerGroups - called without layerConf. Doing nothing.')
      return
    }

    // Leaf node
    if (!layerConf.layers) {
      const olChildLayer = await createOlLayerFromLayerConf(layerConf)

      if (!olChildLayer) {
        console.warn(
          'No OL child layer created somehow. Not doing anything',
          layerConf.titleid,
          layerConf.type,
          layerConf
        )
        return
      }

      // Pozi has a close relationship between layer visibility and whether it is selected or not.
      // The LayerTree component can only turn layers on/off, so here we
      // automatically select/unselect the layer at the same time.
      const toggleSelect = (evt: any) => {
        const { oldValue: layerWasVisible, target: olLayer } = evt

        const titleid = olLayer.get('titleid')
        if (!titleid) {
          console.log('Layer event change:visible - titleid not found:', olLayer)
          return
        }

        console.debug('toggleSelect - layer was visible?', titleid, layerWasVisible)

        const shouldSelectLayer = !layerWasVisible
        if (shouldSelectLayer) {
          console.debug('toggleSelect - dispatch selected', titleid, shouldSelectLayer)

          window.store.dispatch(setLayerSelected(titleid))
        } else {
          console.debug('toggleSelect - dispatch UNselected', titleid, shouldSelectLayer)

          window.store.dispatch(setLayerUnselected(titleid))
        }
      }

      olChildLayer.on('change:visible', toggleSelect)

      // TODO: find a way to do this without needing to store the layerConf in the OL layer
      olChildLayer.set('layerConf', layerConf)

      return olChildLayer
    }

    // Branche node (i.e. nested node)
    const olLayer = new OLLayerGroup({
      opacity: layerConf.opacity ?? 1,
      maxResolution: layerConf.maxResolution,
      minResolution: layerConf.minResolution,
      visible: true // the group layers are always visible, otherwise the child layers won't show!
    })

    // Set some useful properties to the layer
    olLayer.setProperties({
      title: layerConf.title,
      titleid: layerConf.titleid,
      group: layerConf.group,
      groupid: layerConf.groupid
      // initialised: false // custom property to determine if we have initialised this layer
    })

    const layerConfs = [...layerConf.layers]

    const childLayers: OLLayer[] = []
    for (const childConf of layerConfs) {
      const childLayer = await this.createNestedLayerGroups(childConf)

      console.debug('createNestedLayerGroups - childLayer', layerConf.titleid, childConf.titleid, childLayer)

      if (!childLayer) {
        console.log('createNestedLayerGroups - no childLayer created. Ignoring', childConf.titleid, childConf.type)
        continue
      }

      // Keep track of parent/child relationships
      childLayer.set('parentLayer', olLayer)

      childLayers.push(childLayer)
    }

    // Add child layers to layer group
    olLayer.setLayers(new OLCollection(childLayers))

    // TODO: find a way to do this without needing to store the layerConf in the OL layer
    olLayer.set('layerConf', layerConf)

    console.debug('MapLayerFolder - createNestedLayerGroups', layerConf.titleid, olLayer, layerConf, childLayers)

    return olLayer
  }
}
